import React, { useState, useEffect } from "react";
import { useAuth } from "../context/AuthContext";
import api, { employeesApi, unifiedProjectsApi } from "../lib/api";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Textarea } from "./ui/textarea";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "./ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "./ui/select";
import { toast } from "sonner";
import ClientCombobox from "./ClientCombobox";
import CountrySelector from "./CountrySelector";

// Unified project statuses - single flow from sales to completion
const ALL_STATUSES = [
  // Sales phase
  { value: "proposal_sent", label: "Proposal Sent" },
  { value: "contract_negotiation", label: "Contract Negotiation" },
  { value: "contract_signed", label: "Contract Signed" },
  // PM phase (after contract_signed)
  { value: "sent_to_pool", label: "In Pool" },
  { value: "in_progress", label: "In Progress" },
  { value: "awaiting_client", label: "Awaiting Client" },
  { value: "awaiting_payment", label: "Awaiting Payment" },
  { value: "completed", label: "Completed" },
];

// For backwards compatibility
const DEAL_STATUSES = ALL_STATUSES.slice(0, 3); // Sales statuses only for creation

/**
 * Unified Create Project Dialog
 * Used across SalesDashboard, ProjectsPage, and UnifiedProjectsPage
 * 
 * @param {boolean} open - Dialog open state
 * @param {function} onOpenChange - Callback when dialog state changes
 * @param {function} onSuccess - Callback when project is created successfully
 * @param {string} defaultPhase - Default phase ("sales" or "production")
 * @param {string} defaultMonth - Default month in YYYY-MM format
 */
const CreateProjectDialog = ({ 
  open, 
  onOpenChange, 
  onSuccess,
  defaultPhase = "sales",
  defaultMonth = null,
}) => {
  const { user } = useAuth();
  const [loading, setLoading] = useState(false);
  const [clients, setClients] = useState([]);
  const [salesManagers, setSalesManagers] = useState([]);
  const [pmManagers, setPmManagers] = useState([]);
  
  const [formData, setFormData] = useState({
    name: "",
    description: "",
    client_id: "",
    client_name: "",
    phase: defaultPhase,
    status: "proposal_sent",
    probability: 50,
    country: "",
    notes: "",
    sales_manager_id: "",
    project_manager_id: "",
  });

  useEffect(() => {
    if (open) {
      fetchData();
      // Reset form with defaults
      setFormData(prev => ({
        ...prev,
        name: "",
        description: "",
        client_id: "",
        client_name: "",
        phase: defaultPhase,
        status: "proposal_sent",
        probability: 50,
        country: "",
        notes: "",
        sales_manager_id: "",
        project_manager_id: "",
      }));
    }
  }, [open, defaultPhase]);

  const fetchData = async () => {
    try {
      const [clientsRes, empRes] = await Promise.all([
        api.get("/sales-clients"),
        employeesApi.getAll(),
      ]);
      
      setClients(clientsRes.data || []);
      
      const employees = empRes.data || [];
      setSalesManagers(employees.filter(e => e.position === "sales" && e.is_active));
      setPmManagers(employees.filter(e => 
        ["project_manager", "admin", "pm_lead"].includes(e.position) && e.is_active
      ));
    } catch (error) {
      console.error("Error fetching data:", error);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.name.trim()) {
      toast.error("Project name is required");
      return;
    }
    
    try {
      setLoading(true);
      
      const submitData = {
        name: formData.name,
        description: formData.description || undefined,
        client_id: formData.client_id || undefined,
        client_name: formData.client_name || undefined,
        phase: formData.phase,
        country: formData.country || undefined,
        notes: formData.notes || undefined,
      };
      
      // Add phase-specific fields
      if (formData.phase === "sales") {
        submitData.status = formData.status;
        submitData.probability = formData.probability;
        submitData.sales_manager_id = formData.sales_manager_id || undefined;
        submitData.project_manager_id = formData.project_manager_id || undefined;
        if (defaultMonth) {
          submitData.month = defaultMonth;
        }
      } else {
        submitData.status = formData.status;
      }
      
      await unifiedProjectsApi.create(submitData);
      toast.success("Project created successfully");
      onOpenChange(false);
      onSuccess?.();
    } catch (error) {
      console.error("Error creating project:", error);
      toast.error(error.response?.data?.detail || "Failed to create project");
    } finally {
      setLoading(false);
    }
  };

  const isSalesPhase = formData.phase === "sales";
  const canSelectPhase = ["admin", "pm_lead"].includes(user?.role);
  const canSelectSalesManager = ["admin", "sales_director"].includes(user?.role);
  const canSelectPM = ["admin", "pm_lead", "sales_director"].includes(user?.role);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="bg-zinc-900 border-zinc-800 text-zinc-50 max-w-lg">
        <DialogHeader>
          <DialogTitle className="text-zinc-50 font-['Manrope']">
            Create New Project
          </DialogTitle>
        </DialogHeader>
        
        <form onSubmit={handleSubmit} className="space-y-4 mt-2">
          {/* Project Name */}
          <div className="space-y-2">
            <Label className="text-zinc-300">Project Name *</Label>
            <Input
              value={formData.name}
              onChange={(e) => setFormData({ ...formData, name: e.target.value })}
              placeholder="Enter project name"
              className="bg-zinc-950 border-zinc-800 text-zinc-50"
              required
              data-testid="create-project-name"
            />
          </div>
          
          {/* Client */}
          <div className="space-y-2">
            <Label className="text-zinc-300">Client</Label>
            <ClientCombobox
              clients={clients}
              value={formData.client_name}
              selectedId={formData.client_id}
              onChange={(name, id) => setFormData({ 
                ...formData, 
                client_name: name || "", 
                client_id: id || "" 
              })}
              placeholder="Select or type client name..."
            />
          </div>
          
          {/* Phase selector (only for admin/pm_lead) */}
          {canSelectPhase && (
            <div className="space-y-2">
              <Label className="text-zinc-300">Phase</Label>
              <Select
                value={formData.phase}
                onValueChange={(v) => setFormData({ ...formData, phase: v })}
              >
                <SelectTrigger className="bg-zinc-950 border-zinc-800 text-zinc-50">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="bg-zinc-900 border-zinc-800">
                  <SelectItem value="sales">Sales</SelectItem>
                  <SelectItem value="production">Production</SelectItem>
                </SelectContent>
              </Select>
            </div>
          )}
          
          {/* Sales-specific fields */}
          {isSalesPhase && (
            <>
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label className="text-zinc-300">Deal Status</Label>
                  <Select
                    value={formData.status}
                    onValueChange={(v) => {
                      // Auto-adjust probability when status changes
                      const newProbability = v === "contract_signed" ? 100 : formData.probability;
                      setFormData({ ...formData, status: v, probability: newProbability });
                    }}
                  >
                    <SelectTrigger className="bg-zinc-950 border-zinc-800 text-zinc-50">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent className="bg-zinc-900 border-zinc-800">
                      {DEAL_STATUSES.map(s => (
                        <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                
                <div className="space-y-2">
                  <Label className="text-zinc-300">Probability %</Label>
                  <Input
                    type="number"
                    min="0"
                    max="100"
                    value={formData.probability}
                    onChange={(e) => setFormData({ ...formData, probability: parseInt(e.target.value) || 0 })}
                    className="bg-zinc-950 border-zinc-800 text-zinc-50"
                  />
                </div>
              </div>
              
              <div className="space-y-2">
                <Label className="text-zinc-300">Country</Label>
                <CountrySelector
                  value={formData.country}
                  onChange={(country) => setFormData({ ...formData, country: country })}
                  placeholder="Select country..."
                />
              </div>
              
              {/* Sales Manager selector */}
              {canSelectSalesManager && salesManagers.length > 0 && (
                <div className="space-y-2">
                  <Label className="text-zinc-300">Sales Manager</Label>
                  <Select
                    value={formData.sales_manager_id}
                    onValueChange={(v) => setFormData({ ...formData, sales_manager_id: v })}
                  >
                    <SelectTrigger className="bg-zinc-950 border-zinc-800 text-zinc-50">
                      <SelectValue placeholder="Select manager (optional)" />
                    </SelectTrigger>
                    <SelectContent className="bg-zinc-900 border-zinc-800">
                      {salesManagers.map(m => (
                        <SelectItem key={m.id} value={m.id}>{m.name}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              )}
              
              {/* PM selector */}
              {canSelectPM && pmManagers.length > 0 && (
                <div className="space-y-2">
                  <Label className="text-zinc-300">Project Manager</Label>
                  <Select
                    value={formData.project_manager_id}
                    onValueChange={(v) => setFormData({ ...formData, project_manager_id: v === "pool" ? "" : v })}
                  >
                    <SelectTrigger className="bg-zinc-950 border-zinc-800 text-zinc-50">
                      <SelectValue placeholder="Not assigned (Pool)" />
                    </SelectTrigger>
                    <SelectContent className="bg-zinc-900 border-zinc-800">
                      <SelectItem value="pool">
                        <span className="text-orange-400">🎯 Not assigned (Pool)</span>
                      </SelectItem>
                      {pmManagers.map(m => (
                        <SelectItem key={m.id} value={m.id}>{m.name}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <p className="text-xs text-zinc-500">If not assigned, project goes to Pool</p>
                </div>
              )}
            </>
          )}
          
          {/* Production-specific fields */}
          {!isSalesPhase && (
            <div className="space-y-2">
              <Label className="text-zinc-300">Status</Label>
              <Select
                value={formData.status}
                onValueChange={(v) => setFormData({ ...formData, status: v })}
              >
                <SelectTrigger className="bg-zinc-950 border-zinc-800 text-zinc-50">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="bg-zinc-900 border-zinc-800">
                  {ALL_STATUSES.map(s => (
                    <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          )}
          
          {/* Description */}
          <div className="space-y-2">
            <Label className="text-zinc-300">Description</Label>
            <Textarea
              value={formData.description}
              onChange={(e) => setFormData({ ...formData, description: e.target.value })}
              placeholder="Project description..."
              className="bg-zinc-950 border-zinc-800 text-zinc-50 min-h-[80px]"
            />
          </div>
          
          <DialogFooter className="pt-4">
            <Button 
              type="button" 
              variant="outline" 
              onClick={() => onOpenChange(false)}
              className="border-zinc-700 text-zinc-300 hover:bg-zinc-800"
            >
              Cancel
            </Button>
            <Button 
              type="submit" 
              disabled={loading}
              className="bg-orange-500 hover:bg-orange-600 text-white"
              data-testid="create-project-submit"
            >
              {loading ? "Creating..." : "Create Project"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
};

export default CreateProjectDialog;
